import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  combineLatestWith,
  distinctUntilChanged,
  map,
  type Observable,
} from 'rxjs'
import { tap } from 'rxjs/operators'
import { shareLatest } from '@/utils/rx-util'
import {
  type ColorScheme,
  setColorScheme,
  systemColorSchemePreference$,
} from '@/features/shell/theming/color-scheme'
import { type EffectiveTheme, Theme } from '@/features/shell/theming'
import { vivify, getLuminance } from '@/utils/color-util'

/**
 * Manages the application theme.
 */
export class ThemeManager implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Emits the current theme.
   */
  readonly theme$

  /**
   * Emits the current color scheme.
   */
  readonly colorScheme$ = systemColorSchemePreference$.pipe(
    tap(setColorScheme),
    shareLatest(),
  )

  constructor(workspaceTheme$: Observable<Theme>) {
    this.theme$ = workspaceTheme$.pipe(
      distinctUntilChanged((left, right) => Theme.isEqual(left, right)),
      combineLatestWith(this.colorScheme$),
      map(([theme, scheme]) => toEffectiveTheme(theme, scheme)),
      tap(setTheme),
      shareLatest(),
    )

    this._lifecycle
      // Run the theme and color scheme sync.
      .manage(this.theme$)
      .manage(this.colorScheme$)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}

/**
 * Sets the theme variables.
 */
function setTheme(theme: EffectiveTheme) {
  setGlobalCssVar('--theme-base-brand', theme.brand)
  setGlobalCssVar('--theme-base-accent', theme.accent)
  setGlobalCssVar('--button-text-primary', theme.contrastTextColorOverride)
  setGlobalCssVar('--font-color-chip-default', theme.contrastTextColorOverride)
  setGlobalCssVar('--font-color-badge-default', theme.contrastTextColorOverride)
}

/**
 * Sets a CSS variable on the root element.
 *
 * @param variable
 * @param value
 */
function setGlobalCssVar(variable: string, value: string | null | undefined) {
  if (!value) {
    document.documentElement.style.removeProperty(variable)
  } else {
    document.documentElement.style.setProperty(variable, value)
  }
}

/**
 * Adjusts the theme colors if necessary.
 *
 * @param theme
 * @param scheme
 */
export function toEffectiveTheme(
  theme: Theme,
  scheme: ColorScheme,
): EffectiveTheme {
  const effectiveTheme: EffectiveTheme = {
    brand: scheme === 'light' ? theme.brand : theme.brandDark,
    accent: scheme === 'light' ? theme.accent : theme.accentDark,
  }

  const brandLuminance = getLuminance(effectiveTheme.brand)

  if (brandLuminance > 0.7) {
    effectiveTheme.contrastTextColorOverride = 'var(--color-base-black)'
  }

  // For dark mode we need to make sure the colors aren't too dark.
  if (scheme === 'dark') {
    const accentLuminance = getLuminance(effectiveTheme.accent)
    if (brandLuminance < 0.1) {
      effectiveTheme.brand = vivify(effectiveTheme.brand, 0.1)
    }

    if (accentLuminance < 0.1) {
      effectiveTheme.accent = vivify(effectiveTheme.accent, 0.06)
    }
  }

  return effectiveTheme
}
